/* eslint-disable */
import { createSelector } from 'reselect';
import { IStoreState } from 'store';
import { formatNumber } from 'lib/helpers/currency';
import { UNITS, ISummaryProduct, ISummaryPrice, TNullableFloat } from 'store/deal-evaluation/types';
import { truncate } from 'lib/helpers/string-manipulators';
import { mapNameToDisplayName } from 'lib/helpers/field-options';
import { calculateNetOptionValueFromProductSummary } from 'lib/helpers/evaluation-functions';
import { IDealProduct, IFieldOption, IPrice } from 'store/field-options/types';
import { IDealWithEvaluation } from 'store/deal-comparison/types';
import { EPayCycle } from 'store/deals/types';
import { getValueWithCurrency, formatCostToServe, getValueWithCurrencyUnFormatted } from './helpers';
import { ALL_PRODUCTS } from './types';

const dealEvalutionSelectors = (state: IStoreState) => state.dealComparison.dealsWithEvaluation;
const selectedCurrentUnitSelector = (state: IStoreState) => state.dealEvaluation.selectedCurrentUnitType;
const availableProductsSelector = (state: IStoreState) => state.fieldOptions.products;

const UNITS_CENTS_VALUE = 0.01;
const ValueWithCurrencyConst = 2;

const getProductData = (
  products: IDealProduct[] | undefined,
  selectedProduct: { name: string; selected: boolean } | undefined,
  availableProducts: IFieldOption[] | undefined
) =>
  selectedProduct && selectedProduct.name === ALL_PRODUCTS
    ? products
    : products?.filter(product => {
        const productName = availableProducts ? mapNameToDisplayName(product.name, availableProducts) : product.name;
        if (selectedProduct?.name === productName) {
          return product;
        }
        return false;
      });

const getSelectedEvalProductData = (
  evalProduct: ISummaryProduct[] | null,
  selectedProduct: { name: string } | undefined,
  availableProducts: IFieldOption[] | undefined
) =>
  selectedProduct && selectedProduct.name === ALL_PRODUCTS
    ? evalProduct
    : evalProduct?.filter(product => {
        const productName = availableProducts ? mapNameToDisplayName(product.name, availableProducts) : product.name;
        if (selectedProduct?.name === productName) {
          return product;
        }
        return false;
      });

const getIsCurrencyValue = (selectedCurrentUnit: string) => {
  return selectedCurrentUnit === UNITS.DOLLAR || selectedCurrentUnit === UNITS.CENTS;
};

const getCurrencyMultiplierValue = (selectedCurrentUnit: string) => {
  return selectedCurrentUnit === UNITS.CENTS ? UNITS_CENTS_VALUE : 1;
};

type VolumeType = number | undefined | null;

type ProductDataType = ISummaryProduct[] | undefined | null;

const getTotalVolume = (
  isCurrency: boolean,
  volume: VolumeType,
  selectedEvalProductData: ProductDataType,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [getValueWithCurrency(isCurrency, 1, volume)]
    : selectedEvalProductData?.map(({ volume: evalVolume }) => getValueWithCurrency(isCurrency, 1, evalVolume));
};

const getTotalRevenue = (
  isCurrency: boolean,
  volume: VolumeType,
  selectedEvalProductData: ProductDataType,
  salesValue: number | null,
  currencyMultiplier: number,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [getValueWithCurrency(isCurrency, volume, salesValue ? salesValue * currencyMultiplier : 0)]
    : selectedEvalProductData?.map(({ volume: evalVolume, contract }) => {
        const { sales: contractSales } = contract;
        const { ave } = contractSales.total;
        if (!ave) {
          return '-';
        }
        return getValueWithCurrency(isCurrency, evalVolume, ave * currencyMultiplier);
      });
};

const getTotalNetback = (
  isCurrency: boolean,
  volume: VolumeType,
  selectedEvalProductData: ProductDataType,
  netbackValue: number | null,
  currencyMultiplier: number,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [getValueWithCurrency(isCurrency, volume, netbackValue ? netbackValue * currencyMultiplier : 0)]
    : selectedEvalProductData?.map(({ volume: evalVolume, contract }) => {
        const { netback: contractNetback } = contract;
        const { ave } = contractNetback.total;
        if (!ave) {
          return '-';
        }
        return getValueWithCurrency(isCurrency, evalVolume, ave * currencyMultiplier);
      });
};

const getTotalMargin = (
  isCurrency: boolean,
  volume: VolumeType,
  selectedEvalProductData: ProductDataType,
  marginValue: number | null,
  currencyMultiplier: number,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [getValueWithCurrency(isCurrency, volume, marginValue ? marginValue * currencyMultiplier : 0)]
    : selectedEvalProductData?.map(({ volume: evalVolume, contract }) => {
        const { margin: contractMargin } = contract;
        if (!contractMargin) {
          return '-';
        }
        const { ave } = contractMargin.total;
        if (!ave) {
          return '-';
        }
        return getValueWithCurrency(isCurrency, evalVolume, ave * currencyMultiplier);
      });
};

const getTotalMarginAtRisk = (
  isCurrency: boolean,
  volume: VolumeType,
  selectedEvalProductData: ProductDataType,
  marginAtRisk: number | undefined | null,
  currencyMultiplier: number,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [getValueWithCurrency(isCurrency, volume, marginAtRisk ? marginAtRisk * currencyMultiplier : 0)]
    : selectedEvalProductData?.map(({ contract, volume: evalVolume }: ISummaryProduct) => {
        const { marginAtRisk: contractMarginAtRisk } = contract;
        if (!contractMarginAtRisk) {
          return '-';
        }
        if (!volume) {
          return '-';
        }
        return getValueWithCurrency(isCurrency, evalVolume, contractMarginAtRisk * currencyMultiplier);
      });
};

const checkMarginAtRiskWithZero = (marginAtRisk: number | null) => {
  return marginAtRisk === 0;
};

const processContractMarginAtRisk = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData?.map(({ contract, volume: evalVolume }: ISummaryProduct) => {
    const { margin: contractMargin, marginAtRisk: contractMarginAtRisk } = contract;
    if (contractMargin === null || contractMarginAtRisk === null) {
      return '-';
    }
    if (!evalVolume) {
      return '-';
    }
    const { ave } = contractMargin.total;
    if (ave === null) {
      return '-';
    }
    if (ave !== null && contractMarginAtRisk !== null) {
      return contractMarginAtRisk === 0 ? 'NaN' : getValueWithCurrency(isCurrency, 1, ave / contractMarginAtRisk, 1);
    }
    return '-';
  });
};

const processValueWithCurrency = (isCurrency: boolean, marginValue: number | null, marginAtRisk: number | null) => {
  return checkMarginAtRiskWithZero(marginAtRisk)
    ? ['NaN']
    : [getValueWithCurrency(isCurrency, 1, marginValue && marginAtRisk ? marginValue / marginAtRisk : 0, 1)];
};

const getSharpeRatio = (
  isCurrency: boolean,
  selectedEvalProductData: ProductDataType,
  marginValue: number | null,
  marginAtRisk: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? processValueWithCurrency(isCurrency, marginValue, marginAtRisk)
    : processContractMarginAtRisk(selectedEvalProductData, isCurrency);
};

const getHeadLine = (
  isCurrency: boolean,
  selectedEvalProductData: ProductDataType,
  headline: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [getValueWithCurrency(isCurrency, 1, headline)]
    : selectedEvalProductData?.map(({ headline: evalHeadline }) => {
        if (!evalHeadline) {
          return '-';
        }
        return getValueWithCurrency(isCurrency, 1, evalHeadline);
      });
};

const checkValues = (evalVolume: number, contract: ISummaryPrice, mainBatna: ISummaryPrice) => {
  let value = '';
  if (!evalVolume) {
    value = '-';
  }
  if (!contract.margin) {
    value = '-';
  }
  if (!contract?.margin?.total.ave) {
    value = '-';
  }
  if (!mainBatna.margin) {
    value = '-';
  }
  if (!mainBatna?.margin?.total.ave) {
    value = '-';
  }
  return value;
};

const getContractBatna1 = (
  isCurrency: boolean,
  selectedEvalProductData: ProductDataType,
  currencyMultiplier: number,
  isAllProduct: boolean = false
) => {
  let contractBatna1Total = 0;
  let contractBatna1 = selectedEvalProductData
    ? selectedEvalProductData?.map(({ contract, mainBatna, volume: evalVolume }) => {
        const valueString = checkValues(evalVolume, contract, mainBatna);
        if (valueString === '-') {
          return valueString;
        }
        contractBatna1Total +=
          getValueWithCurrencyUnFormatted(
            isCurrency,
            evalVolume,
            ((contract?.margin?.total?.ave ?? 0) - (mainBatna?.margin?.total?.ave ?? 0)) * currencyMultiplier
          ) || 0;

        return (
          getValueWithCurrency(
            isCurrency,
            evalVolume,
            ((contract?.margin?.total?.ave ?? 0) - (mainBatna?.margin?.total?.ave ?? 0)) * currencyMultiplier
          ) || '-'
        );
      })
    : [];
  contractBatna1 = isAllProduct
    ? ['Σ '.concat(formatNumber(contractBatna1Total, 0, true))].concat(contractBatna1)
    : contractBatna1;

  return contractBatna1;
};
const checkEvalVolumeAndContractValue = (evalVolume: number, contract: ISummaryPrice) => {
  return !evalVolume || !contract || !contract.margin || !contract.margin.total.ave;
};
const getContractContractWOOptions = (
  isCurrency: boolean,
  selectedEvalProductData: ProductDataType,
  currencyMultiplier: number
) => {
  return selectedEvalProductData?.map(({ contract, withoutOpts, volume: evalVolume }) => {
    if (
      checkEvalVolumeAndContractValue(evalVolume, contract) ||
      !withoutOpts ||
      !withoutOpts.margin ||
      !withoutOpts.margin.total.ave
    ) {
      return ['-'];
    }
    return (
      getValueWithCurrency(
        isCurrency,
        evalVolume,
        (contract.margin ? contract.margin.total.ave ?? 0 : 0 - withoutOpts.margin.total.ave) * currencyMultiplier
      ) || ['-']
    );
  });
};

const getBasePrices = (selectedProductData: IDealProduct[] | undefined) => {
  return selectedProductData?.map((product: { basePrice: IPrice }) => product.basePrice) ?? [];
};

const getPricingFormulas = (basePrices: IPrice[]) => {
  return basePrices.length > 0
    ? basePrices.map(({ description }) => (description ? truncate(description, { maxLength: 50 }) : '-'))
    : [];
};

const getCostToServeYield = (
  costToServe: number | null,
  selectedEvalProductData: ProductDataType,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? [formatCostToServe(costToServe)]
    : selectedEvalProductData?.map(({ contract }) => formatCostToServe(contract.yield));
};

const getReimbursedSupplyCost = (selectedEvalProductData: ProductDataType) => {
  return selectedEvalProductData?.map(({ contract }) => formatNumber(contract.cost_reimbursed, 0) || '-');
};

const getVariableSupplyCost = (selectedEvalProductData: ProductDataType) => {
  return selectedEvalProductData?.map(({ contract }) => formatNumber(contract.cost_freight ?? 0, 0) || '-');
};

const getFixedSupplyCost = (basePrices: IPrice[]) => {
  return basePrices.length > 0 ? basePrices.map(({ costFixed }) => costFixed?.toString() ?? '-') : ['-'];
};

const getAdditionalInventory = (basePrices: IPrice[]) => {
  return basePrices.length > 0 ? basePrices.map(({ costIncInventory }) => costIncInventory?.toString() ?? '-') : ['-'];
};

const getAdditionalTransitDay = (basePrices: IPrice[]) => {
  return basePrices.map(({ costTransitDays }) => costTransitDays ?? '-');
};

type SelectedProductType = IDealProduct[] | undefined | null;

const getModeOfTransport = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map((product: { transportMode: string }) => product.transportMode || '-');
};

const getIncoTerm = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map((product: { incoTerm: string }) => product.incoTerm || '-');
};

const getOtherSupplyItems = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map((product: { otherSupply: string }) => product.otherSupply || '-');
};

const getCredit = (selectedEvalProductData: ProductDataType) => {
  return selectedEvalProductData
    ? selectedEvalProductData.map(({ creditCost }) => formatNumber(creditCost, 0, true))
    : ['-'];
};

const checkPayCycleWithMonth = (payDelay: number, payCycle: EPayCycle | null) => {
  return payCycle === 'month' ? `EOM + ${payDelay} days` : `Net + ${payDelay} days`;
};

const getCreditTerm = (payDelay: number | null, payCycle: EPayCycle | null) => {
  return payDelay ? checkPayCycleWithMonth(payDelay, payCycle) : '-';
};

const getProbabilityOfDefault = (probDefault: number | null) => {
  return probDefault ? `${probDefault}%` : '-';
};

const getExposure = (selectedEvalProductData: ProductDataType) => {
  return selectedEvalProductData
    ? selectedEvalProductData.map(({ exposure: evalExposure }) => formatNumber(evalExposure, 0, true))
    : ['-'];
};

const getOtherCreditItems = (otherCredit: string | null | undefined) => {
  return otherCredit ?? '-';
};

const checkCapValue = (capValue: TNullableFloat, isCurrency: boolean, volume: number | null) => {
  return capValue ? getValueWithCurrency(isCurrency, volume, capValue, 0) : '-';
};

const mapCapData = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData
        .map(
          ({ options: { cap: evalCap }, volume: evalVolume }) =>
            (evalCap && getValueWithCurrency(isCurrency, evalVolume, evalCap.ave ?? 0, 0)) || '-'
        )
        .pop()
    : '-';
};

const getCapValue = (
  capValue: TNullableFloat | null,
  selectedEvalProductData: ProductDataType,
  isCurrency: boolean,
  volume: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct ? checkCapValue(capValue, isCurrency, volume) : mapCapData(selectedEvalProductData, isCurrency);
};

const checkFloorValue = (floorValue: number | null, isCurrency: boolean, volume: number | null) => {
  return floorValue ? getValueWithCurrency(isCurrency, volume, floorValue, 0) : '-';
};

const mapFloorData = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData
        .map(({ options: { floor: evalFloor }, volume: evalVolume }) =>
          evalFloor ? getValueWithCurrency(isCurrency, evalVolume, evalFloor.ave ?? 0, 0) : '-'
        )
        .pop()
    : '-';
};

const getFloorValue = (
  floorValue: number | null,
  selectedEvalProductData: ProductDataType,
  isCurrency: boolean,
  volume: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? checkFloorValue(floorValue, isCurrency, volume)
    : mapFloorData(selectedEvalProductData, isCurrency);
};

const checkPushValue = (pushEval: number | null, isCurrency: boolean, volume: number | null) => {
  return pushEval ? getValueWithCurrency(isCurrency, volume, pushEval, 0) : '-';
};

const mapPushData = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData
        .map(({ options: { push }, volume: evalVolume }) =>
          push ? getValueWithCurrency(isCurrency, evalVolume, push.ave ?? 0, 0) : '-'
        )
        .pop()
    : '-';
};

const getPushVal = (
  pushEval: number | null,
  selectedEvalProductData: ProductDataType,
  isCurrency: boolean,
  volume: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct ? checkPushValue(pushEval, isCurrency, volume) : mapPushData(selectedEvalProductData, isCurrency);
};

const checkPulEValue = (pulEval: number | null, isCurrency: boolean, volume: number | null) => {
  return pulEval ? getValueWithCurrency(isCurrency, volume, pulEval, 0) : '-';
};

const mapPulEvalData = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData
        .map(
          ({ options: { pull }, volume: evalVolume }) =>
            (pull && getValueWithCurrency(isCurrency, evalVolume, pull.ave ?? 0, 0)) || '-'
        )
        .pop()
    : '-';
};

const getPullVal = (
  pulEval: number | null,
  selectedEvalProductData: ProductDataType,
  isCurrency: boolean,
  volume: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? checkPulEValue(pulEval, isCurrency, volume)
    : mapPulEvalData(selectedEvalProductData, isCurrency);
};

const checkExtensionsValue = (extensionValue: number | null, isCurrency: boolean, volume: number | null) => {
  return extensionValue ? getValueWithCurrency(isCurrency, volume, extensionValue, 0) : '-';
};

const mapExtensionsData = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData
        .map(
          ({ options: { extension }, volume: evalVolume }) =>
            (extension && getValueWithCurrency(isCurrency, evalVolume, extension.ave ?? 0, 0)) || '-'
        )
        .pop()
    : '-';
};

const getExtensions = (
  extensionValue: number | null,
  selectedEvalProductData: ProductDataType,
  isCurrency: boolean,
  volume: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? checkExtensionsValue(extensionValue, isCurrency, volume)
    : mapExtensionsData(selectedEvalProductData, isCurrency);
};

const checkEarlyTerminationValue = (earlyTerm: number | null, isCurrency: boolean, volume: number | null) => {
  return earlyTerm ? getValueWithCurrency(isCurrency, volume, earlyTerm, 0) : '-';
};

const mapEarlyTerminationData = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData
        .map(
          ({ options: { earlyTerm: evalEarlyTerm }, volume: evalVolume }) =>
            (evalEarlyTerm && getValueWithCurrency(isCurrency, evalVolume, evalEarlyTerm.ave ?? 0, 0)) || '-'
        )
        .pop()
    : '-';
};

const getEarlyTermination = (
  earlyTerm: number | null,
  selectedEvalProductData: ProductDataType,
  isCurrency: boolean,
  volume: number | null,
  isAllProduct: boolean = false
) => {
  return isAllProduct
    ? checkEarlyTerminationValue(earlyTerm, isCurrency, volume)
    : mapEarlyTerminationData(selectedEvalProductData, isCurrency);
};

const getPriceSwitch = (selectedProductData: SelectedProductType) => {
  return (
    selectedProductData?.map(
      (product: { priceSwitch: { description: string } }) => product.priceSwitch?.description
    ) ?? ['-']
  );
};

const getOfferTypes = (offerType: string | null) => {
  return offerType ?? '-';
};

const getOptionsValue = (selectedEvalProductData: ProductDataType, isCurrency: boolean) => {
  return selectedEvalProductData
    ? selectedEvalProductData.map(productSummary =>
        getValueWithCurrency(
          isCurrency,
          productSummary.volume,
          calculateNetOptionValueFromProductSummary(productSummary) || 0,
          ValueWithCurrencyConst
        )
      )
    : ['-'];
};

const getDates = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map(({ startDate, endDate }) => ({ startDate, endDate }));
};

const getCapDescriptions = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map(({ capEnabled, capDescription }) => {
    if (capEnabled && capDescription) {
      return capDescription;
    }
    return '-';
  });
};

const getFloorDescriptions = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map(({ floorEnabled, floorDescription }) => {
    if (floorEnabled && floorDescription) {
      return floorDescription;
    }
    return '-';
  });
};

const getPushDescription = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map(({ push }) => {
    if (push?.enabled && push.description) {
      return push.description;
    }
    return '-';
  });
};

const getPullDescription = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map(({ pull }) => {
    if (pull?.enabled && pull.description) {
      return pull.description;
    }
    return '-';
  });
};

const getExtensionDescription = (selectedProductData: SelectedProductType) => {
  return selectedProductData?.map(({ extension }) => {
    if (extension?.enabled && extension.description) {
      return extension.description;
    }
    return '-';
  });
};

export const getDealEvaluationData = createSelector(
  [dealEvalutionSelectors, selectedCurrentUnitSelector, availableProductsSelector],
  (dealsWithEvaluation, selectedCurrentUnit, availableProducts) =>
    dealsWithEvaluation.map(({ deal, evaluation }: IDealWithEvaluation) => {
      const isCurrency = getIsCurrencyValue(selectedCurrentUnit);
      const currencyMultiplier = getCurrencyMultiplierValue(selectedCurrentUnit);
      const {
        products,
        customer,
        offerType,
        probDefault,
        otherCredit,
        name,
        status,
        payCycle,
        payDelay,
        nonstandardRisks,
        productName,
        id,
      } = deal || [];
      const {
        volume,
        headline,
        sales,
        margin,
        marginAtRisk,
        netback,
        cap: capValue,
        floor: floorValue,
        extension: extensionValue,
        earlyTerm,
        push: pushEval,
        pull: pulEval,
        products: evalProduct,
        yield: costToServe,
      } = evaluation || [];
      const { ave: salesValue } = sales || 0;
      const { ave: marginValue } = margin || 0;
      const { ave: netbackValue } = netback || 0;

      const findProductName = () => {
        return productName?.length === 1
          ? productName[0]
          : productName?.find((pd: { selected: boolean; name: string }) => pd.selected);
      };

      const selectedProduct = productName ? findProductName() : { selected: true, name: '' };

      const isAllProduct = selectedProduct && selectedProduct.name === ALL_PRODUCTS;

      const selectedProductData: IDealProduct[] | undefined = getProductData(
        products,
        selectedProduct,
        availableProducts
      );

      const selectedEvalProductData: ProductDataType = getSelectedEvalProductData(
        evalProduct,
        selectedProduct,
        availableProducts
      );

      const totalVolume = getTotalVolume(isCurrency, volume, selectedEvalProductData, isAllProduct);

      const totalRevenue = getTotalRevenue(
        isCurrency,
        volume,
        selectedEvalProductData,
        salesValue,
        currencyMultiplier,
        isAllProduct
      );

      const totalNetback = getTotalNetback(
        isCurrency,
        volume,
        selectedEvalProductData,
        netbackValue,
        currencyMultiplier,
        isAllProduct
      );

      const totalMargin = getTotalMargin(
        isCurrency,
        volume,
        selectedEvalProductData,
        marginValue,
        currencyMultiplier,
        isAllProduct
      );

      const totalMarginAtRisk: string[] | undefined = getTotalMarginAtRisk(
        isCurrency,
        volume,
        selectedEvalProductData,
        marginAtRisk,
        currencyMultiplier,
        isAllProduct
      );

      const sharpeRatio: string[] | undefined = getSharpeRatio(
        isCurrency,
        selectedEvalProductData,
        marginValue,
        marginAtRisk,
        isAllProduct
      );

      const headLine = getHeadLine(isCurrency, selectedEvalProductData, headline, isAllProduct);
      //contract - batna1 (margin in $) = (contract - batna1) * volume
      //contract - batna1 (margin in $/T) = (contract - batna1)
      const contractBatna1 = getContractBatna1(isCurrency, selectedEvalProductData, currencyMultiplier, isAllProduct);

      const contractContractWOOptions = getContractContractWOOptions(
        isCurrency,
        selectedEvalProductData,
        currencyMultiplier
      );

      const basePrices = getBasePrices(selectedProductData);

      const pricingFormulas = getPricingFormulas(basePrices);

      const costToServeYield = getCostToServeYield(costToServe, selectedEvalProductData, isAllProduct);

      const totalSupplyCost = '-'; // need to update later

      const reimbursedSupplyCost = getReimbursedSupplyCost(selectedEvalProductData);

      const variableSupplyCost = getVariableSupplyCost(selectedEvalProductData);

      const fixedSupplyCost = getFixedSupplyCost(basePrices);

      const additionalInventory = getAdditionalInventory(basePrices);

      const additionalTransitDay = getAdditionalTransitDay(basePrices);
      const modeOfTransport = getModeOfTransport(selectedProductData);

      const incoTerm = getIncoTerm(selectedProductData);

      const otherSupplyItems = getOtherSupplyItems(selectedProductData);

      const credit = getCredit(selectedEvalProductData);

      const creditTerm = getCreditTerm(payDelay, payCycle);

      const probabilityOfDefault = getProbabilityOfDefault(probDefault);

      const exposure = getExposure(selectedEvalProductData);

      const otherCreditItems = getOtherCreditItems(otherCredit);

      const cap = getCapValue(capValue, selectedEvalProductData, isCurrency, volume, isAllProduct);

      const floor = getFloorValue(floorValue, selectedEvalProductData, isCurrency, volume, isAllProduct);

      const customerFlex = '-';
      const pushVal = getPushVal(pushEval, selectedEvalProductData, isCurrency, volume, isAllProduct);

      const pullVal = getPullVal(pulEval, selectedEvalProductData, isCurrency, volume, isAllProduct);

      const extensions = getExtensions(extensionValue, selectedEvalProductData, isCurrency, volume, isAllProduct);

      const earlyTermination = getEarlyTermination(
        earlyTerm,
        selectedEvalProductData,
        isCurrency,
        volume,
        isAllProduct
      );

      const priceSwitch = getPriceSwitch(selectedProductData);

      const offerTypes = getOfferTypes(offerType);

      const optionsValue = getOptionsValue(selectedEvalProductData, isCurrency);

      const dates = getDates(selectedProductData);

      const capDescriptions = getCapDescriptions(selectedProductData);

      const floorDescriptions = getFloorDescriptions(selectedProductData);

      const customerFlexDescription = '-';
      const pushDescription = getPushDescription(selectedProductData);

      const pullDescription = getPullDescription(selectedProductData);

      const extensionDescription = getExtensionDescription(selectedProductData);

      const earlyTerminationDescription = '-'; // either early termiantion or extension
      const priceSwitchDescription = '-';
      return {
        id,
        name,
        products,
        customer,
        status,
        dates,
        productName,
        totalVolume,
        totalRevenue,
        totalNetback,
        totalMargin,
        totalMarginAtRisk,
        pricingFormulas,
        offerTypes,
        headLine,
        contractBatna1,
        costToServeYield,
        totalSupplyCost,
        reimbursedSupplyCost,
        variableSupplyCost,
        fixedSupplyCost,
        additionalInventory,
        additionalTransitDay,
        modeOfTransport,
        incoTerm,
        otherSupplyItems,
        creditTerm,
        credit,
        probabilityOfDefault,
        exposure,
        otherCreditItems,
        optionsValue,
        cap,
        floor,
        customerFlex,
        pushVal,
        pullVal,
        extensions,
        earlyTermination,
        priceSwitch,
        capDescriptions,
        floorDescriptions,
        customerFlexDescription,
        pushDescription,
        pullDescription,
        extensionDescription,
        earlyTerminationDescription,
        priceSwitchDescription,
        nonstandardRisks,
        header: '',
        sharpeRatio,
        contractContractWOOptions,
      };
    })
);
